import { Box } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useMobile } from "lib/@components/mobile"
import { useReference } from "lib/@hooks/use-reference"
import { useEffect } from "react"
import { ScheduleBreadcrumbs } from "routes/schedule/components/schedule-breadcrumbs"
import { ScheduleFilter } from "routes/schedule/components/schedule-filter"
import { ScheduleSort } from "routes/schedule/components/schedule-sort"
import { useStack } from "routes/schedule/lib/useStack"
import { UpDirectory } from "routes/schedule/tree/schedules/upDirectory"
import { ScheduleSelectorToolbar, ScheduleSelectorWrapper } from "slot-definitions"
import { TopBox } from "lib/@components/topBox"
import { useTreeItemIndex } from "routes/schedule/lib/useTreeItemIndex"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { ScheduleSelectorComponent } from "routes/schedule/components/schedule-selector-component"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { useSearchParams } from "react-router-dom"
import { clean } from "lib/clean"
import { RegimeGenerated } from "event-definitions"
import { navigate } from "lib/routes/navigate"
import { PlanRoot } from "library/tree/constants.mjs"
import { parentFind } from "library/tree/index.mjs"
import { getIsUnlocked } from "routes/regime/assets/controller/register-locked.controller"
import { useStoreState } from "lib/store"
import { updateRegimeLock } from "routes/regime/assets/regime-lock-store"
import { hasRegisterLink, isSyncedToRegister } from "./register-sync.mjs"

export { hasRegisterLink, isSyncedToRegister }

ScheduleSelectorToolbar("*").plug(<ScheduleSort priority={90} />)
ScheduleSelectorToolbar("*").plug(<ScheduleFilter priority={80} />)

export function ScheduleSelector() {
    const [searchParams, setSearchParams] = useSearchParams({ id: "_root" })
    const parentId = searchParams.get("id")

    const isMobile = useMobile()
    const stack = useStack(parentId)
    useReference({}, "search", "sortMode")
    const index = useTreeItemIndex()

    const parentItem = useTreeItem(parentId, true)
    const regimeRoot = parentFind(parentItem, (c) => c[PlanRoot]) ?? parentItem
    const targetId = regimeRoot?.id
    const isUnlocked = getIsUnlocked.useResults(targetId)
    const regimeLockKey = `regimeLock.${targetId ?? "__none"}`
    const [lockState] = useStoreState(regimeLockKey, () => isUnlocked)

    useEffect(() => {
        if (targetId && isUnlocked !== undefined) {
            updateRegimeLock(targetId, isUnlocked)
        }
    }, [targetId, isUnlocked])
    RegimeGenerated.useEvent(() => {
        if (targetId) {
            const params = new URLSearchParams(window.location.search)
            params.set("id", targetId)
            navigate(`/app/schedules?${params.toString()}`)
        }
    }, [parentItem])

    const isRegimeSyncedToRegister = isSyncedToRegister(regimeRoot)

    return index._root ? (
        <Bound
            parentId={parentId}
            parentItem={parentItem}
            isRegimeSyncedToRegister={isRegimeSyncedToRegister}
            isUnlocked={lockState}
        >
            <ScheduleSelectorWrapper.Wrapper>
                <Box
                    display="flex"
                    flexDirection="column"
                    height={1}
                    sx={{ overflowY: "auto", overflowX: "visible" }}
                    flex={1}
                    mx={2}
                >
                    <TopBox inContainer>
                        <ScheduleBreadcrumbs
                            sx={{ ml: isMobile ? 0 : 1, mt: 0, mb: 1 }}
                            current={parentId}
                            stack={stack}
                        />
                        <UpDirectory sx={{ flex: 0 }} itemId={stack[0]} onClick={pop} />
                    </TopBox>
                    <ScheduleSelectorComponent id={parentId} onSelect={select} />
                </Box>
            </ScheduleSelectorWrapper.Wrapper>
        </Bound>
    ) : (
        <LoadingScreen description="Has Tree Index" />
    )

    function pop() {
        window.history.back()
    }

    async function select(item) {
        if (item.children || item.hasChildren) {
            const currentSearchParams = new URLSearchParams(window.location.search)
            setSearchParams(
                clean({
                    ...Object.fromEntries(currentSearchParams.entries()),
                    id: item.id,
                })
            )
        }
    }
}
